import type { LayoutType } from "@ziggapp/klap-core/layout";
import type { VideoStylePresetModel } from "@ziggapp/klap-core/video";
import { klap } from "../utils/klap";

// "reframe-focus-default" experiment: for new web users in the test arm, every
// Klap template reads as Focus = "Everyone" instead of "Speaker". klap-api
// applies the same override when it generates their shorts (the template's
// Focus is not sent by the web, it's resolved server-side); here it's applied
// where the editor reads Klap templates (Base, Reset, template apply, unsaved
// changes count), so a test-arm short never looks "modified" against its
// template and clicking Base never switches it back to Speaker. User
// templates keep their saved value. Delete with the experiment.

export type ReframeFocusVariant = "control" | "test";

// Must equal klap-api's REFRAME_FOCUS_EXPERIMENT_LAUNCH_DATE and the flag's
// signed_up_at release condition in PostHog.
export const REFRAME_FOCUS_EXPERIMENT_LAUNCH_DATE = new Date(
  "2026-09-30T11:00:00Z",
);

// Mirrors klap-core's apply/reframe.ts rule (not exported): "Everyone" needs
// the multi-frame layouts. Klap templates already allow all of them.
const EVERYONE_FOCUS_LAYOUT_TYPES: LayoutType[] = ["split", "split-3", "split-4"];

// Returns a COPY (the react-query cache keeps the stored template). Nested
// objects are reused as-is, so nanoid-stamped subfields keep their ids and
// the template doesn't diff as "changed" against the short.
export const withEveryoneFocus = <
  T extends Pick<VideoStylePresetModel, "config">,
>(
  preset: T,
): T => ({
  ...preset,
  config: {
    ...preset.config,
    reframe: {
      ...preset.config.reframe,
      speakerFocus: false,
      allowedLayoutTypes: klap.layout.canonicalizeAllowedLayoutTypes([
        ...preset.config.reframe.allowedLayoutTypes,
        ...EVERYONE_FOCUS_LAYOUT_TYPES,
      ]),
    },
  },
});

export const applyReframeFocusVariant = <
  T extends Pick<VideoStylePresetModel, "config" | "isKlapPreset">,
>(
  preset: T,
  variant: ReframeFocusVariant,
): T =>
  variant === "test" &&
  preset.isKlapPreset === true &&
  preset.config?.reframe?.speakerFocus === true
    ? withEveryoneFocus(preset)
    : preset;
